import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  DisableMainCategory,
  ErrorBoundary,
  FourHCFourVC,
  PaginationButtonGroup,
  Section,
  SectionOnTopTitle,
  TwoTwoOTC,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionBackground,
  getSectionLink,
  getSectionSubtitle,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
  getMetadataImage,
  getHreflangData,
  getPagination,
} from '../../store/template/feedPage/feedPageMumbai/feedpageMumbai.selectors';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { getPageMetaText } from '../templates.utils';

interface FeedPageMumbaiProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const feedSectionArticles = getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION)(state) || [];
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state) || [];
  return [
    ...feedSectionArticles,
    ...topSectionArticles,
  ];
};

export const FeedPageMumbaiComponent: React.FunctionComponent<FeedPageMumbaiProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageMumbaiContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit * 1.5}px`,
    marginBottom: `${baseUnit}px`,
    justifyItems: 'center',
  },
});

export const FeedPageMumbaiContentConnected: React.FunctionComponent = () => {
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const feedSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const commercialSectionTitle = useSelector(getSectionTitleByKey(SECTION_NAMES.COMMERCIAL_SECTION));
  const commercialSectionSubtitle = useSelector(getSectionSubtitle);
  const commercialSectionImage = useSelector(getSectionBackground);
  const commercialSectionLink = useSelector(getSectionLink);
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const hreflangData = useSelector(getHreflangData) || [];
  const paginationData = useSelector(getPagination);
  const { prevButtonText, prevButtonURL, nextButtonText, nextButtonURL, currentPage } = paginationData;
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags {...getPageMetaText(metaTitle, metaDescription, currentPage)} articles={articles} pageImage={pageImage} alternateUrls={hreflangData} />
      <HeadScripts />
      <Theme>
        <DisableMainCategory>
          <AppContent>
            <NavigationLayout mainWrapperConfigThemeKey="mainContent">
              <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
              <div className={css(style.sectionsWrapper)}>

                <Section themeConfigurationPropKey="firstSectionMaxWidth">
                  <SectionOnTopTitle
                    title={commercialSectionTitle}
                    subtitle={commercialSectionSubtitle}
                    image={commercialSectionImage}
                    link={commercialSectionLink}
                  />
                </Section>

                <Section>
                  <TwoTwoOTC cards={topSectionArticles} paddingFactors={{ large: 1, medium: 1, small: 0.75 }} />
                </Section>

                <Section>
                  <FourHCFourVC cards={feedSectionArticles} paddingFactors={{ large: 1, medium: 1, small: 0.75 }} />
                </Section>

              </div>
              {Object.keys(paginationData).length === 0
                  ? <LoadMoreFeedPostsButtonConnected />
                  : <PaginationButtonGroup prevText={prevButtonText} prevURL={prevButtonURL} nextText={nextButtonText} nextURL={nextButtonURL} />}
              <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
            </NavigationLayout>
          </AppContent>
        </DisableMainCategory>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
